import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../FirebaseConfig"; // Your Firebase config
import { doc, getDoc, updateDoc } from "firebase/firestore";
import Header from "./Header";
import Footer from "./Footer";
import {
  FaCrown,
  FaFacebook,
  FaFlag,
  FaLinkedin,
  FaPen, // Icon for writing a review
  FaStar,
  FaTwitter,
} from "react-icons/fa";
import { FaUpwork, FaXTwitter } from "react-icons/fa6";
import { RiArrowRightUpLine } from "react-icons/ri";
import { TbBrandFiverr } from "react-icons/tb";
import { toast } from "react-toastify";
import { getFlagEmoji } from "./TechTalentProgram";

const FreelancerDetails = () => {
  const { id } = useParams(); // Get freelancer ID from URL
  const [freelancer, setFreelancer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewerName, setReviewerName] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [rating, setRating] = useState(0);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false); // Toggle form visibility

  // Fetch freelancer data from Firestore
  useEffect(() => {
    const fetchFreelancer = async () => {
      try {
        const docRef = doc(db, "freelancer", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setFreelancer({ id: docSnap.id, ...docSnap.data() });
        } else {
          toast.error("Freelancer not found");
        }
      } catch (error) {
        console.error("Error fetching freelancer:", error);
        toast.error("Failed to load freelancer details");
      } finally {
        setLoading(false);
      }
    };

    fetchFreelancer();
  }, [id]);

  // Generate WhatsApp link for hiring
  const generateWhatsAppLink = (freelancerName) => {
    const phoneNumber = "+923204192824";
    const message = `Hi Anum, I want to hire ${freelancerName}`;
    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  };

  // Handle review submission
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setIsSubmittingReview(true);

    try {
      if (!reviewerName || !reviewComment || rating === 0) {
        toast.error("Please fill all fields and provide a rating.");
        return;
      }

      const review = {
        reviewer: reviewerName,
        comment: reviewComment,
        rating: rating,
        date: new Date().toISOString(),
      };

      const docRef = doc(db, "freelancer", id);
      await updateDoc(
        docRef,
        {
          reviews: freelancer.reviews
            ? [...freelancer.reviews, review]
            : [review],
        },
        { merge: true }
      );

      setReviewerName("");
      setReviewComment("");
      setRating(0);
      setShowReviewForm(false); // Hide form after submission
      toast.success("Review submitted successfully!");
      setFreelancer((prev) => ({
        ...prev,
        reviews: prev.reviews ? [...prev.reviews, review] : [review],
      }));
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-logo"></div>
      </div>
    );
  }

  if (!freelancer) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl text-gray-600">Freelancer not found</p>
      </div>
    );
  }

  return (
    <>
      <Header />
      <section className="relative bg-gray-100 py-12 md:pb-24">
        <div className="container mx-auto px-6 md:px-24">
          {/* Hero Section */}
          <div className="relative flex flex-col md:flex-row items-center bg-white rounded-lg shadow-xl p-8 mb-8">
            <div className="relative mb-6 md:mb-0 md:mr-8">
              <img
                src={freelancer.pictureUrl || "https://via.placeholder.com/150"}
                alt={freelancer.name}
                className="w-32 h-32 md:w-48 md:h-48 rounded-full object-cover border-4 border-logo"
              />
              <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full border-2 border-white shadow-lg">
                <span className="text-2xl flex justify-center items-center pt-1">
                  {getFlagEmoji(freelancer.region)}
                </span>
              </div>
            </div>
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 flex-wrap">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                  {freelancer.name}
                </h1>

                {freelancer.applyForInterview === 'pass' && (
                  <div className="absolute top-3 right-3  bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg uppercase z-10 flex items-center gap-1">
                    <FaCrown className="text-white" /> Featured
                  </div>
                )}
              </div>

              <h2 className="text-xl md:text-2xl text-logo font-semibold">
                {freelancer.expertise}
              </h2>
              <div className="flex justify-center md:justify-start items-center gap-1 mt-2">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="text-yellow-400" />
                ))}
                <span className="ml-2 text-gray-600">(5.0)</span>
              </div>
              <p className="text-gray-600 mt-2">
                {freelancer.region || "Global"}
              </p>
            </div>
          </div>

          {/* Details Section */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Left Column: Bio and Skills */}
            <div className="md:col-span-2">
              {/* About Section */}
              <div className="bg-white rounded-lg shadow-xl p-6 mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">About</h3>
                <p className="text-gray-600 leading-relaxed">
                  {freelancer.about || "No bio available."}
                </p>
              </div>

              {/* Skills Section */}
              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Skills
                </h3>
                <div className="flex flex-wrap gap-2">
                  {freelancer.skills?.length > 0 ? (
                    freelancer.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="bg-gradient-to-r from-black to-logo text-white px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-600">No skills listed.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Contact and Links */}
            <div className="bg-white rounded-lg shadow-xl p-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Contact & Links
              </h3>
              <a
                href={generateWhatsAppLink(freelancer.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-full text-center font-semibold shadow-lg mb-4 hover:from-orange-600 hover:to-red-600 transition-all"
              >
                Hire Me
              </a>
              {freelancer.portfolio && (
                <a
                  href={freelancer.portfolio}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-logo font-semibold mb-2 hover:underline"
                >
                  Portfolio <RiArrowRightUpLine className="ml-1" />
                </a>
              )}
              <div className="flex flex-col gap-4 mt-4">
                {freelancer.socialLinks?.linkedin && (
                  <a
                    href={freelancer.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-2 text-blue-500 hover:text-blue-600"
                  >
                    <FaLinkedin size={24} />
                    {freelancer.socialLinks.linkedin}
                  </a>
                )}
                {freelancer.socialLinks?.upwork && (
                  <a
                    href={freelancer.socialLinks.upwork}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-2 text-green-500 hover:text-green-600"
                  >
                    <FaUpwork size={24} />
                    {freelancer.socialLinks.upwork}
                  </a>
                )}
                {freelancer.socialLinks?.twitter && (
                  <a
                    href={freelancer.socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-2 text-black hover:text-gray-800"
                  >
                    <FaXTwitter size={24} />
                    {freelancer.socialLinks.twitter}
                  </a>
                )}
                {freelancer.socialLinks?.facebook && (
                  <a
                    href={freelancer.socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-2 text-logo hover:text-logo-dark"
                  >
                    <FaFacebook size={24} />
                    {freelancer.socialLinks.facebook}
                  </a>
                )}
                {freelancer.socialLinks?.fiver && (
                  <a
                    href={freelancer.socialLinks.fiver}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-2 text-green-500 hover:text-green-600"
                  >
                    <TbBrandFiverr size={24} />
                    {freelancer.socialLinks.fiver}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="bg-white rounded-lg shadow-xl p-6 mt-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Reviews</h3>
            <div className="space-y-4">
              {freelancer.reviews?.length > 0 ? (
                freelancer.reviews.map((review, index) => (
                  <div key={index} className="border-b py-4">
                    <p className="font-semibold">{review.reviewer}</p>
                    <p className="text-gray-600">{review.comment}</p>
                    <div className="flex gap-1 mt-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <FaStar key={i} className="text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(review.date).toLocaleDateString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-gray-600">No reviews yet.</p>
              )}
            </div>
            {/* Write a Review Button */}
            <button
              className="mt-4 flex items-center text-logo font-semibold hover:text-logo-dark transition-colors"
              onClick={() => setShowReviewForm(true)}
            >
              <FaPen className="mr-2" /> Write a Review
            </button>
          </div>

          {/* Write a Review Form (Toggleable) */}
          {showReviewForm && (
            <div className="bg-white rounded-lg shadow-xl p-6 mt-4 transition-all duration-300 ease-in-out transform origin-top">
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <input
                  type="text"
                  placeholder="Your Name"
                  className="w-full p-2 border rounded"
                  value={reviewerName}
                  onChange={(e) => setReviewerName(e.target.value)}
                  required
                />
                <textarea
                  placeholder="Write your review..."
                  className="w-full p-2 border rounded h-24 resize-none"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  required
                />
                <div className="flex items-center gap-2">
                  <label className="text-gray-700">Rating:</label>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={`cursor-pointer ${
                        rating >= star ? "text-yellow-400" : "text-gray-300"
                      }`}
                      onClick={() => setRating(star)}
                    />
                  ))}
                </div>
                <div className="flex justify-between">
                  <button
                    type="button"
                    className="text-gray-600 hover:text-gray-800 font-semibold"
                    onClick={() => setShowReviewForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 ${
                      isSubmittingReview
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-black to-logo"
                    } text-white rounded`}
                    disabled={isSubmittingReview}
                  >
                    {isSubmittingReview ? "Submitting..." : "Submit Review"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default FreelancerDetails;
